<template>
  <div class="phoneInput">
    <label v-if="!hideLabels" for="phone">{{ $t('questionForm.phone.title') }}</label>
    <v-row no-gutters>
      <v-col class="flex-grow-0">
        <UlSelect
          v-model="phoneCountry"
          :disabled="disabled"
          :menu-props="{
            minWidth: $vuetify.breakpoint.smAndUp ? '120px' : '94%',
            contentClass: 'my-1'
          }"
          :options="countries"
          :padded-spacer="!$vuetify.breakpoint.smAndUp"
          :tabindex="-1"
          hide-details
          item-text="code"
          item-value="code"
          return-object
          @focusin="$emit('focusinPrefix')"
        >
          <template #optionPrepend="{ item }">
            <v-img
              :src="$asset(`img/countryFlags/${getFlagImage(item)}.svg`)"
              class="mr-2"
              contain
              max-width="16px"
            />
          </template>
          <template #optionTitle="{ item }">
            {{ item.prefix }}
          </template>
          <template #selection="{ item }">
            <v-img
              :src="$asset(`img/countryFlags/${getFlagImage(item)}.svg`)"
              class="mr-2"
              contain
              max-width="16px"
            />
            {{ item.prefix }}
          </template>
        </UlSelect>
      </v-col>
      <v-col class="pt-2 pt-sm-0 pl-sm-2">
        <v-text-field
          id="phone"
          v-model="phoneNumber"
          :autofocus="autofocus"
          :class="{
            'is-valid':
              !disabled &&
              $v.phoneNumber.$dirty &&
              !$v.phoneNumber.$invalid &&
              ((!requiredInput && phoneNumber.length) || requiredInput)
          }"
          :data-cy="inputCypressData"
          :disabled="disabled"
          :error="!disabled && !!phoneErrorMessage.length"
          :placeholder="showPlaceholder ? placeholderText : ''"
          autocomplete="off"
          flat
          hide-details
          pattern="\d*"
          solo
          @blur="onPhoneNumberBlur"
          @focus="handleFocusWithGtm"
          @focusin="$emit('focusinNumber')"
          @keyup="$emit('keyup', $event)"
        >
          <template v-if="!disabled" #append>
            <v-icon v-if="phoneNumber.length && $v.phoneNumber.$dirty && !$v.phoneNumber.$error">
              $tick
            </v-icon>
            <v-icon v-if="$v.phoneNumber.$error">$warning</v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <div
      v-if="!disabled"
      :class="phoneErrorMessage.length ? 'error--text' : 'grey-darken-1--text text-body-small'"
      :data-cy="messageCypressData"
      class="mt-1"
    >
      {{ !!phoneErrorMessage.length ? phoneErrorMessage : showHelpText ? message : '' }}
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { phone } from '@/customVuelidate';
import formValidation from '@/mixins/formValidation';
import formGtm from '@/mixins/formGtm';
import { countryCallingCodes } from '@/assets/js/country-codes';
export default {
  name: 'Phone',
  mixins: [formValidation, formGtm],
  validations() {
    if (this.requiredInput) {
      return {
        phoneNumber: { required, phone: phone(this.phoneCountry.prefix) }
      };
    } else {
      if (this.phoneNumber?.length) {
        return {
          phoneNumber: { phone: phone(this.phoneCountry.prefix) }
        };
      }
    }
    return { phoneNumber: {} };
  },
  props: {
    autofocus: {
      type: Boolean,
      default: false
    },
    defaultPhone: {
      type: Object,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableGtm: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    hideLabels: {
      type: Boolean,
      default: false
    },
    inputCypressData: {
      type: String,
      default: ''
    },
    messageCypressData: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    prefixSelected: {
      type: String,
      required: false
    },
    requiredInput: {
      type: Boolean,
      default: true
    },
    showHelpText: {
      type: Boolean,
      default: true
    },
    showPlaceholder: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      required: false
    },
    eventSufix: {
      type: String,
      default: ''
    }
  },
  computed: {
    phoneNumber: {
      get() {
        return this.$store.state.forms.phone.phoneNumber;
      },
      set(value) {
        this.$store.commit('forms/phone/updatePhoneNumber', value);
      }
    },
    phoneCountry: {
      get() {
        return this.$store.state.forms.phone.phoneCountry;
      },
      set(value) {
        this.$store.commit('forms/phone/updatePhoneCountry', value);
      }
    },
    placeholderText() {
      return this.placeholder || this.phoneExample;
    },
    message() {
      return this.helpText || this.$t('questionForm.phone.helpText');
    },
    phoneErrorMessage() {
      if (!this.$v.$dirty || (!this.requiredInput && !this.phoneNumber.length)) {
        return '';
      }

      if (this.requiredInput && !this.phoneNumber.length) {
        return this.errorText.length ? this.errorText : this.$t('questionForm.phone.errorText');
      } else if (this.$v.phoneNumber.$error) {
        return this.$t('questionForm.phone.errorPhoneText', { exampleNumber: this.phoneExample });
      }
      return '';
    },
    phoneExample() {
      return this.phoneCountry.example;
    },
    countries() {
      return countryCallingCodes;
    }
  },
  created() {
    this.$eventHub.$on(`validatePhone${this.eventSufix}`, done =>
      this.validateForm(done, 'forms/phone')
    );
    if (this.prefixSelected) {
      this.setPrefix(this.prefixSelected);
    }

    if (this.defaultPhone) {
      this.setPrefix(this.defaultPhone.prefix);
      this.phoneNumber = this.defaultPhone.number.toString();
    }
  },
  beforeDestroy() {
    this.$eventHub.$off(`validatePhone${this.eventSufix}`);
  },
  methods: {
    setPrefix(prefix) {
      let countryByPrefix = countryCallingCodes.find(val => {
        return val.prefix === prefix;
      });
      if (countryByPrefix) {
        this.phoneCountry = countryByPrefix;
      }
    },
    onPhoneNumberBlur() {
      if (!this.$v.phoneNumber.$dirty && !this.phoneNumber) {
        return;
      }

      this.$v.phoneNumber.$touch();

      if (!this.$v.phoneNumber.$error) {
        const parsed = this.phoneFormatted(`${this.phoneCountry.prefix}${this.phoneNumber}`);
        this.phoneNumber = `${parsed.number}`;
      }
    },
    handleFocusWithGtm(event) {
      if (!this.disableGtm) {
        this.sendGtmEvent('Telefon');
      }
      if (!event?.target || typeof event.target.scrollIntoView !== 'function') {
        return;
      }
      const target = event.target;
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }
};
</script>

<style lang="scss" scoped>
.phoneInput {
  ::v-deep .row {
    @media #{map-get($display-breakpoints, 'xs-only')} {
      flex-direction: column;
    }
  }

  ::v-deep .ul-select {
    @media #{map-get($display-breakpoints, 'sm-and-up')} {
      width: 120px;
    }
  }

  ::v-deep .v-input__control {
    width: 7.5rem;
  }
}
</style>
